import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { format } from 'date-fns';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Styled components for the referral form
const ReferralContainer = styled(Box)(({ theme }) => ({
  fontFamily: 'Courier New, monospace',
  fontSize: '12px',
  lineHeight: '1.4',
  backgroundColor: 'white',
  padding: '20px',
  maxWidth: '800px',
  margin: '0 auto',
  '& .header': {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    marginBottom: '20px',
    borderBottom: '2px solid #000',
    paddingBottom: '10px'
  },
  '& .section': {
    marginBottom: '15px'
  },
  '& .section-title': {
    fontWeight: 'bold',
    borderBottom: '1px solid #000',
    paddingBottom: '5px',
    marginBottom: '10px'
  },
  '& .field-row': {
    display: 'flex',
    marginBottom: '5px'
  },
  '& .field-label': {
    fontWeight: 'bold',
    minWidth: '120px',
    marginRight: '10px'
  },
  '& .field-value': {
    flex: 1
  },
  '& .checkbox-section': {
    marginTop: '10px',
    '& .MuiFormControlLabel-label': {
      fontFamily: 'Courier New, monospace',
      fontSize: '12px',
      lineHeight: '1.4'
    }
  },
  '& .signature-line': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: '40px'
  },
  '& .signature-field': {
    display: 'inline-block',
    borderBottom: '1px solid #000',
    minWidth: '200px',
    marginLeft: '10px'
  }
}));

interface Patient {
  id: string;
  name: string;
  dateOfBirth: string;
  gender?: string;
  contact?: string;
  email?: string;
  address?: string;
  bloodType?: string;
  allergies?: string[];
  chronicConditions?: string[];
  mrn?: string;
  insurance?: string;
  policyNumber?: string;
  primaryLanguage?: string;
}

interface Facility {
  id: string;
  name: string;
  phone?: string;
  npi?: string;
  address?: string;
  practice?: string;
}

interface Provider {
  name: string;
  practice: string;
  phone: string;
  npi: string;
  email: string;
}

interface ReferralFormProps {
  open: boolean;
  onClose: () => void;
  patient: Patient;
  referringProvider: Provider;
  receivingProvider: Provider;
  clinicalDetails: {
    reasonForReferral: string;
    primaryDiagnosis: string;
    pertinentHistory: string;
    testsOrdered: string;
    requestedAction: string;
  };
  attachments: {
    encounterNote: boolean;
    growthChart: boolean;
    ekgReport: boolean;
    insuranceAuth: boolean;
    imagingCD: boolean;
  };
  followUp: {
    callPatient: boolean;
    emailReport: boolean;
    faxNote: boolean;
  };
  notes?: string;
}

export const ReferralForm: React.FC<ReferralFormProps> = ({
  open,
  onClose,
  patient,
  referringProvider,
  receivingProvider,
  clinicalDetails,
  attachments,
  followUp,
  notes,
}) => {
  const [referralId] = React.useState(
    () => `RF-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`
  );
  const currentDate = format(new Date(), 'yyyy-MM-dd HH:mm');

  // State for attachments checkboxes
  const [encounterNote, setEncounterNote] = React.useState(attachments.encounterNote);
  const [growthChart, setGrowthChart] = React.useState(attachments.growthChart);
  const [ekgReport, setEkgReport] = React.useState(attachments.ekgReport);
  const [insuranceAuth, setInsuranceAuth] = React.useState(attachments.insuranceAuth);
  const [imagingCD, setImagingCD] = React.useState(attachments.imagingCD);

  // State for follow-up checkboxes
  const [callPatient, setCallPatient] = React.useState(followUp.callPatient);
  const [emailReport, setEmailReport] = React.useState(followUp.emailReport);
  const [faxNote, setFaxNote] = React.useState(followUp.faxNote);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Referral Form</title>
            <style>
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                line-height: 1.4; 
                margin: 20px; 
              }
              .header { text-align: center; font-weight: bold; font-size: 16px; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
              .section { margin-bottom: 15px; }
              .section-title { font-weight: bold; border-bottom: 1px solid #000; padding-bottom: 5px; margin-bottom: 10px; }
              .field-row { display: flex; margin-bottom: 5px; }
              .field-label { font-weight: bold; min-width: 120px; margin-right: 10px; }
              .field-value { flex: 1; }
              .checkbox-section { margin-top: 10px; }
              .signature-line { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 40px; }
              .signature-field { display: inline-block; border-bottom: 1px solid #000; min-width: 200px; margin-left: 10px; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <div class="header">REFERRAL FORM</div>
            <div class="section">
              <div class="field-row">
                <span class="field-label">Date:</span>
                <span class="field-value">{currentDate}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Referral ID:</span>
                <span class="field-value">{referralId}</span>
              </div>
            </div>

            <Divider />

            <div class="section">
              <div class="section-title">Patient Information:</div>
              <div class="field-row">
                <span class="field-label">Name:</span>
                <span class="field-value">{patient.name}</span>
              </div>
              <div class="field-row">
                <span class="field-label">DOB:</span>
                <span class="field-value">{format(new Date(patient.dateOfBirth), 'MM/dd/yyyy')}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Gender:</span>
                <span class="field-value">{patient.gender || 'Not specified'}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Phone:</span>
                <span class="field-value">{patient.contact || 'Not provided'}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Email:</span>
                <span class="field-value">{patient.email || 'Not provided'}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Primary Language:</span>
                <span class="field-value">{patient.primaryLanguage || 'English'}</span>
              </div>
              <div class="field-row">
                <span class="field-label">MRN:</span>
                <span class="field-value">{patient.mrn || 'Not assigned'}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Insurance:</span>
                <span class="field-value">{patient.insurance || 'Not provided'}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Policy #:</span>
                <span class="field-value">{patient.policyNumber || 'Not provided'}</span>
              </div>
            </div>

            <Divider />

            <div class="section">
              <div class="section-title">Referring Provider:</div>
              <div class="field-row">
                <span class="field-label">Name:</span>
                <span class="field-value">{referringProvider.name}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Practice:</span>
                <span class="field-value">{referringProvider.practice}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Phone:</span>
                <span class="field-value">{referringProvider.phone}</span>
              </div>
              <div class="field-row">
                <span class="field-label">NPI:</span>
                <span class="field-value">{referringProvider.npi}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Direct Email:</span>
                <span class="field-value">{referringProvider.email}</span>
              </div>
            </div>

            <Divider />

            <div class="section">
              <div class="section-title">Receiving Provider (Referral To):</div>
              <div class="field-row">
                <span class="field-label">Specialty:</span>
                <span class="field-value">{receivingProvider.practice}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Referred To:</span>
                <span class="field-value">{receivingProvider.name}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Facility:</span>
                <span class="field-value">{receivingProvider.practice}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Phone:</span>
                <span class="field-value">{receivingProvider.phone}</span>
              </div>
              <div class="field-row">
                <span class="field-label">Address:</span>
                <span class="field-value">{patient.address || 'Address not provided'}</span>
              </div>
            </div>

            <Divider />

            <div class="section">
              <div class="section-title">Clinical Details:</div>
              <div class="field-row">
                <span class="field-label">Reason for Referral:</span>
              </div>
              <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
                {clinicalDetails.reasonForReferral}
              </div>
              
              <div class="field-row">
                <span class="field-label">Primary Diagnosis:</span>
              </div>
              <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
                {clinicalDetails.primaryDiagnosis}
              </div>
              
              <div class="field-row">
                <span class="field-label">Pertinent History:</span>
              </div>
              <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
                {clinicalDetails.pertinentHistory}
              </div>
              
              <div class="field-row">
                <span class="field-label">Tests Already Ordered:</span>
              </div>
              <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
                {clinicalDetails.testsOrdered}
              </div>
              
              <div class="field-row">
                <span class="field-label">Requested Action:</span>
              </div>
              <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
                {clinicalDetails.requestedAction}
              </div>
            </div>

            <Divider />

            <div class="section-title">Attachments:</div>
            <div class="checkbox-section">
              <div><span class="checkbox">${encounterNote ? '☑' : '☐'}</span> Encounter Note</div>
              <div><span class="checkbox">${growthChart ? '☑' : '☐'}</span> Growth Chart</div>
              <div><span class="checkbox">${ekgReport ? '☑' : '☐'}</span> EKG Report</div>
              <div><span class="checkbox">${insuranceAuth ? '☑' : '☐'}</span> Insurance Auth</div>
              <div><span class="checkbox">${imagingCD ? '☑' : '☐'}</span> Imaging CD</div>
            </div>

            <Divider />

            <div class="section-title">Follow-up:</div>
            <div class="checkbox-section">
              <div><span class="checkbox">${callPatient ? '☑' : '☐'}</span> Call Patient</div>
              <div><span class="checkbox">${emailReport ? '☑' : '☐'}</span> Email Report</div>
              <div><span class="checkbox">${faxNote ? '☑' : '☐'}</span> Fax Note</div>
            </div>

            <Divider />

            <div class="section">
              <div class="section-title">Notes:</div>
              <div style={{ marginLeft: '10px', marginBottom: '10px' }}>
                {notes || 'Please contact us if additional documentation is needed. This referral is valid for 90 days.'}
              </div>
            </div>

            <Divider />

            <div class="section">
              <div class="section-title">Signature:</div>
              <div class="signature-line">
                <div style={{ width: '65%' }}>
                  <div style={{ borderBottom: '1px solid #000', height: '1px' }}></div>
                  <div style={{ marginTop: '5px' }}>{referringProvider.name}</div>
                </div>
                <div>Date: {currentDate}</div>
              </div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleGeneratePDF = async () => {
    const element = document.getElementById('referral-form-content');
    if (!element) return;

    try {
      // Create a clone of the element to avoid affecting the original
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      clone.style.width = '800px';
      clone.style.backgroundColor = 'white';
      clone.style.padding = '20px';
      document.body.appendChild(clone);

      // Generate canvas from the cloned element
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: clone.scrollHeight
      });

      // Remove the clone
      document.body.removeChild(clone);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      const fileName = `Referral_Form_${patient.name.replace(/\s+/g, '_')}_${currentDate}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        style: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Referral Form</Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleGeneratePDF}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              Print
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <ReferralContainer id="referral-form-content">
          <div className="header">
            REFERRAL FORM
          </div>
          
          <div className="section">
            <div className="field-row">
              <span className="field-label">Date:</span>
              <span className="field-value">{currentDate}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Referral ID:</span>
              <span className="field-value">{referralId}</span>
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Patient Information:</div>
            <div className="field-row">
              <span className="field-label">Name:</span>
              <span className="field-value">{patient.name}</span>
            </div>
            <div className="field-row">
              <span className="field-label">DOB:</span>
              <span className="field-value">{format(new Date(patient.dateOfBirth), 'MM/dd/yyyy')}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Gender:</span>
              <span className="field-value">{patient.gender || 'Not specified'}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Phone:</span>
              <span className="field-value">{patient.contact || 'Not provided'}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Email:</span>
              <span className="field-value">{patient.email || 'Not provided'}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Primary Language:</span>
              <span className="field-value">{patient.primaryLanguage || 'English'}</span>
            </div>
            <div className="field-row">
              <span className="field-label">MRN:</span>
              <span className="field-value">{patient.mrn || 'Not assigned'}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Insurance:</span>
              <span className="field-value">{patient.insurance || 'Not provided'}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Policy #:</span>
              <span className="field-value">{patient.policyNumber || 'Not provided'}</span>
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Referring Provider:</div>
            <div className="field-row">
              <span className="field-label">Name:</span>
              <span className="field-value">{referringProvider.name}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Practice:</span>
              <span className="field-value">{referringProvider.practice}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Phone:</span>
              <span className="field-value">{referringProvider.phone}</span>
            </div>
            <div className="field-row">
              <span className="field-label">NPI:</span>
              <span className="field-value">{referringProvider.npi}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Direct Email:</span>
              <span className="field-value">{referringProvider.email}</span>
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Receiving Provider (Referral To):</div>
            <div className="field-row">
              <span className="field-label">Specialty:</span>
              <span className="field-value">{receivingProvider.practice}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Referred To:</span>
              <span className="field-value">{receivingProvider.name}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Facility:</span>
              <span className="field-value">{receivingProvider.practice}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Phone:</span>
              <span className="field-value">{receivingProvider.phone}</span>
            </div>
            <div className="field-row">
              <span className="field-label">Address:</span>
              <span className="field-value">{patient.address || 'Address not provided'}</span>
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Clinical Details:</div>
            <div className="field-row">
              <span className="field-label">Reason for Referral:</span>
            </div>
            <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
              {clinicalDetails.reasonForReferral}
            </div>
            
            <div className="field-row">
              <span className="field-label">Primary Diagnosis:</span>
            </div>
            <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
              {clinicalDetails.primaryDiagnosis}
            </div>
            
            <div className="field-row">
              <span className="field-label">Pertinent History:</span>
            </div>
            <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
              {clinicalDetails.pertinentHistory}
            </div>
            
            <div className="field-row">
              <span className="field-label">Tests Already Ordered:</span>
            </div>
            <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
              {clinicalDetails.testsOrdered}
            </div>
            
            <div className="field-row">
              <span className="field-label">Requested Action:</span>
            </div>
            <div style={{ marginLeft: '130px', marginBottom: '10px' }}>
              {clinicalDetails.requestedAction}
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Attachments:</div>
            <Box className="checkbox-section">
              <FormControlLabel
                control={<Checkbox checked={encounterNote} onChange={e => setEncounterNote(e.target.checked)} />}
                label="Encounter Note"
              />
              <FormControlLabel
                control={<Checkbox checked={growthChart} onChange={e => setGrowthChart(e.target.checked)} />}
                label="Growth Chart"
              />
              <FormControlLabel
                control={<Checkbox checked={ekgReport} onChange={e => setEkgReport(e.target.checked)} />}
                label="EKG Report"
              />
              <FormControlLabel
                control={<Checkbox checked={insuranceAuth} onChange={e => setInsuranceAuth(e.target.checked)} />}
                label="Insurance Auth"
              />
              <FormControlLabel
                control={<Checkbox checked={imagingCD} onChange={e => setImagingCD(e.target.checked)} />}
                label="Imaging CD"
              />
            </Box>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Follow-up:</div>
            <Box className="checkbox-section">
              <FormControlLabel
                control={<Checkbox checked={callPatient} onChange={e => setCallPatient(e.target.checked)} />}
                label="Call Patient"
              />
              <FormControlLabel
                control={<Checkbox checked={emailReport} onChange={e => setEmailReport(e.target.checked)} />}
                label="Email Report"
              />
              <FormControlLabel
                control={<Checkbox checked={faxNote} onChange={e => setFaxNote(e.target.checked)} />}
                label="Fax Note"
              />
            </Box>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Notes:</div>
            <div style={{ marginLeft: '10px', marginBottom: '10px' }}>
              {notes || 'Please contact us if additional documentation is needed. This referral is valid for 90 days.'}
            </div>
          </div>

          <Divider />

          <div className="section">
            <div className="section-title">Signature:</div>
            <div className="signature-line">
              <div style={{ width: '65%' }}>
                <div style={{ borderBottom: '1px solid #000', height: '1px' }}></div>
                <div style={{ marginTop: '5px' }}>{referringProvider.name}</div>
              </div>
              <div>Date: {currentDate}</div>
            </div>
          </div>
        </ReferralContainer>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}; 